import angular from 'angular';
import moment from 'moment';
import { IMovimentacaoPayload } from '../../../models/movimentacoes.service.model';
import { IMovimentacaoPeriodo } from '../../../models/movimentacoes-periodos.service.model';
import {
    IMovimentacaoQuickAddBindings,
    IMovimentacaoQuickAddForm,
    TMovimentacaoQuickAddSubmitMode
} from './movimentacoes-quick-add.model';
import { IMovimentacaoFormState } from '../movimentacao-form/movimentacoes-movimentacao-form.model';
import {
    createEmptyMovimentacaoForm,
    isMovimentacaoDataPagamentoEnabled,
    isMovimentacaoPeriodoEncerrado,
    isMovimentacaoPeriodoMesAtual,
    normalizeMovimentacaoDate
} from '../../helpers/movimentacoes-form.helpers';
import {
    isMovimentacaoEventoDias,
    normalizeMovimentacaoValor,
    resolveMovimentacaoEntityIdentifier
} from '../../../helpers/movimentacoes-value.helpers';

export class MovimentacoesQuickAddController implements IMovimentacaoQuickAddBindings {
    public selectedPeriod: IMovimentacaoPeriodo | null = null;
    public submitting = false;
    public form: IMovimentacaoQuickAddForm = createEmptyMovimentacaoForm();
    public quickAddForm: angular.IFormController;
    public formValid = false;
    public vigenciaFinalInvalida = false;
    public dataPagamentoEnabled = isMovimentacaoDataPagamentoEnabled();
    public onSubmit: (args: { payload: IMovimentacaoPayload; mode: TMovimentacaoQuickAddSubmitMode }) => angular.IPromise<any> = () => null as any;
    public onClose: () => void = () => { /* */ };

    public $onInit(): void {
        this.resetFormForSelectedPeriod(true);
    }

    public $onChanges(changes: angular.IOnChangesObject): void {
        if (changes.selectedPeriod) {
            this.resetFormForSelectedPeriod(false);
        }
    }

    public closeCard(): void {
        this.clearForm();
        if (this.onClose) {
            this.onClose();
        }
    }

    public clearForm(): void {
        this.resetFormForSelectedPeriod(false);
        this.restoreSuggestedDataPagamento();
    }

    public onFormValidityChange(state: IMovimentacaoFormState): void {
        this.formValid = !!state.valid;
        this.vigenciaFinalInvalida = !!state.vigenciaFinalInvalida;
    }

    public isSubmitDisabled(): boolean {
        return this.submitting ||
            !this.selectedPeriod ||
            isMovimentacaoPeriodoEncerrado(this.selectedPeriod) ||
            !this.quickAddForm ||
            !this.formValid ||
            this.vigenciaFinalInvalida;
    }

    public submit(mode: TMovimentacaoQuickAddSubmitMode): void {
        if (!this.onSubmit || !this.selectedPeriod) {
            return;
        }

        if (this.quickAddForm) {
            this.quickAddForm.$setSubmitted();
        }
        if (this.isSubmitDisabled()) {
            return;
        }

        const payload = this.buildPayload();

        const promise = this.onSubmit({ payload, mode });
        if (!promise || typeof (promise as any).then !== 'function') {
            return;
        }

        promise
            .then(() => {
                this.clearForm();
            })
            .catch(() => {
                // erros são tratados no controller pai.
            });
    }

    private resetFormForSelectedPeriod(withSuggestion: boolean): void {
        this.form = this.buildFormForSelectedPeriod(withSuggestion);
        this.formValid = false;
        this.vigenciaFinalInvalida = false;
        if (this.quickAddForm) {
            this.quickAddForm.$setPristine();
            this.quickAddForm.$setUntouched();
        }
    }

    private buildFormForSelectedPeriod(withSuggestion: boolean): IMovimentacaoQuickAddForm {
        const form = createEmptyMovimentacaoForm();

        if (!this.selectedPeriod) {
            return form;
        }

        if (withSuggestion && this.dataPagamentoEnabled && isMovimentacaoPeriodoMesAtual(this.selectedPeriod)) {
            form.datapagamento = this.buildSuggestedInitDataPagamento();
        }

        return form;
    }

    private restoreSuggestedDataPagamento(): void {
        if (!this.dataPagamentoEnabled) {
            return;
        }

        if (!this.selectedPeriod || !isMovimentacaoPeriodoMesAtual(this.selectedPeriod)) {
            return;
        }

        if (this.form.datapagamento) {
            return;
        }

        this.form.datapagamento = this.buildSuggestedDataPagamentoForClear();
    }

    private buildSuggestedInitDataPagamento(): string {
        return moment().format('YYYY-MM-DD');
    }

    private buildSuggestedDataPagamentoForClear(): string {
        return moment().format('DD/MM/YYYY');
    }

    private buildPayload(): IMovimentacaoPayload {
        const trabalhadorId = resolveMovimentacaoEntityIdentifier(this.form.trabalhador, 'trabalhador', 'codigo');
        const eventoId = resolveMovimentacaoEntityIdentifier(this.form.evento, 'evento', 'codigo');
        const lotacaoId = resolveMovimentacaoEntityIdentifier(this.form.lotacao, 'lotacao', 'codigo');
        const datapagamento = normalizeMovimentacaoDate(this.form.datapagamento);
        const valor = this.normalizeValor();

        const payload: IMovimentacaoPayload = {
            movimentacaoperiodo: this.selectedPeriod!.movimentacaoperiodo,
            trabalhador: trabalhadorId || '',
            evento: eventoId || '',
            lotacao: lotacaoId || '',
            valor: valor === null ? '' : valor,
            referencia: this.form.referencia ? this.form.referencia.trim() : null,
            datapagamento: this.dataPagamentoEnabled ? (datapagamento || null) : null,
            vigencia: this.form.vigencia
        };

        if (this.form.vigencia === 3) {
            payload.vigenciamesfinal = Number(this.form.vigenciamesfinal);
            payload.vigenciaanofinal = Number(this.form.vigenciaanofinal);
        }

        return payload;
    }

    private normalizeValor(): string | number | null {
        const normalized = normalizeMovimentacaoValor(this.form.valor, this.form.evento);

        if (normalized === null) {
            return null;
        }

        if (this.isEventoDias()) {
            const numeric = Number(normalized);
            if (!Number.isFinite(numeric) || numeric < 0 || !Number.isInteger(numeric)) {
                return null;
            }
        }

        return normalized;
    }

    private isEventoDias(): boolean {
        return isMovimentacaoEventoDias(this.form.evento);
    }
}
